import { capitalize } from '@/utils/capitalize';
import i18n from '../i18n';
import { displayDate } from '../utils/dates';
import { Direction } from '../views/Layout/Direction';
import { TimeDelimiter } from '../views/School/CourseSections/Assignments/Students/ExtendedDueDateDialog';
import { AssignmentAttempt, isActiveAttempt } from './AssignmentAttempt';
import { AssignmentAverageGrade } from './AssignmentAverageGrade';
import { AssignmentGrade } from './AssignmentGrade';
import { AssignmentGroup } from './AssignmentGroup';
import { AssignmentLO } from './AssignmentLO';
import { AssignmentRange } from './AssignmentRange';
import { AssignmentSource } from './AssignmentSource';
import { translateBookForTransport, translateBookFromTransport } from './Book';
import { BookOutline } from './BookOutline';
import { Chapter, getChapterById } from './Chapter';
import { Company } from './Company';
import { translateContentFromTransport } from './Content';
import { ContentOutline } from './ContentOutline';
import { AvailabilityEndBasis, CourseSection } from './CourseSection';
import { DiscussionRequirements } from './Discussions';
import { Enrollment } from './Enrollment';
import { Item } from './Item';
import { Module } from './Module';
import { Page, getPageById } from './Page';
import { Product } from './Product';
import { ProductBookOutline } from './ProductBookOutline';
import { Question, QuestionDifficulty, QuestionFormat, QuestionSource } from './Question';
import { QuestionAssignmentGrade } from './QuestionAssignmentGrade';
import { AssignmentReferenceType } from './Reference';
import { Term } from './Term';

export interface AnswerLength {
  minWords?: number;
  maxWords?: number;
}

export interface TestConfiguration {
  lockdownBrowser?: boolean;
  preventScreenshot?: boolean;
}

export interface TutorRequirements {
  loCount?: number;
  loMinPrompts?: number;
  loMaxPrompts?: number;
}

export enum AssignmentType {
  READING_COMPREHENSION = 'READING_COMPREHENSION',
  QUIZ = 'QUIZ',
  STUDY_GUIDE = 'STUDY_GUIDE',
  TEST = 'TEST',
  DISCUSSION = 'DISCUSSION',
  TUTOR = 'TUTOR',
}

export enum FinalGradeMethod {
  AVERAGE = 'AVERAGE',
  HIGHEST = 'HIGHEST',
}

export enum GradeSource {
  EFFORT = 'EFFORT',
  ACCURACY = 'ACCURACY',
}

export interface QuestionList {
  profiles: QuestionProfile[];
}

export enum QuestionOriginType {
  PRODUCT = 'PRODUCT',
  CONTENT = 'CONTENT',
  ANY = 'ANY',
}

export const DefaultQuestionList = {
  profiles: [
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
    { originType: QuestionOriginType.ANY },
  ],
};

export const DefaultQuestionBank: QuestionBank = {
  difficulty: [QuestionDifficulty.EASY, QuestionDifficulty.MEDIUM, QuestionDifficulty.HARD],
  source: [QuestionSource.AI, QuestionSource.PUBLISHER, QuestionSource.INSTRUCTOR],
  formats: [QuestionFormat.MULTIPLE_CHOICE, QuestionFormat.TRUE_FALSE],
};

export interface QuestionProfile {
  questionId?: number;
  originType?: QuestionOriginType;
  originId?: number;
}

export interface QuestionBank {
  difficulty: QuestionDifficulty[];
  source: QuestionSource[];
  formats?: QuestionFormat[];
}

export interface Assignment extends Item {
  /* Basic school stuff */
  schoolId: number;
  school?: Company;

  termId: number;
  term?: Term;

  courseSectionId?: number;
  courseSection?: CourseSection;

  assignmentGroupId: number;
  assignmentGroup?: AssignmentGroup;

  search?: string;

  points: number;
  availableAt: Date;
  gradeAt: Date;
  dueAt: Date;
  lmsId?: string;
  sisId?: string;
  name: string;
  type?: AssignmentType;

  gradeSource: GradeSource;

  numberOfQuestions: number;
  isIncludeAllQuestions: boolean;

  maxAttempts: number;
  timeLimit: number;

  attempts?: AssignmentAttempt[];

  ebScheduleAvailableAt?: string;
  ebScheduleDueAt3Days?: string;
  ebScheduleDueAt3Hours?: string;
  ebScheduleResults?: string;

  finalGradeMethod: FinalGradeMethod;

  grades?: AssignmentGrade[];

  averageGrade?: AssignmentAverageGrade;

  questionGrades?: QuestionAssignmentGrade[];

  moduleId?: number;

  module?: Module;

  sources: AssignmentSource[];

  questionList: QuestionList;

  questionBank: QuestionBank;

  isOpened?: boolean;
  isClosed?: boolean;
  openedAt?: Date;
  closedAt?: Date;

  question?: Question;
  studentEnrollmentCount?: number;

  description?: string;

  answerLength?: AnswerLength;
  discussions?: DiscussionRequirements;

  tests?: TestConfiguration;
  tutors?: TutorRequirements;
  assignmentLOs?: AssignmentLO[];
}

export const displayAssignmentType = (type: AssignmentType | undefined): string => {
  if (!type) {
    return '-';
  }

  if (type === AssignmentType.STUDY_GUIDE) {
    return i18n.t('study');
  } else if (type === AssignmentType.READING_COMPREHENSION) {
    return i18n.t('reading');
  } else if (type === AssignmentType.TEST) {
    return i18n.t('test');
  } else if (type === AssignmentType.QUIZ) {
    return i18n.t('quiz');
  } else if (type === AssignmentType.DISCUSSION) {
    return i18n.t('discussion');
  } else if (type === AssignmentType.TUTOR) {
    return i18n.t('tutor');
  }

  return '';
};

export const displayAssignment = (assignment?: Assignment) => {
  if (!assignment || !assignment?.type) {
    return '-';
  }

  const { sources, type } = assignment;

  const typeOut = ` ${capitalize(type.toLowerCase())}`;

  if (!sources || sources.length === 0) {
    return '';
  }

  const source = sources[0];
  let ranges: AssignmentRange[] = [];
  if (source) {
    const { referenceType, startId, endId, startLabel, endLabel } = source;
    ranges = [{ type: referenceType, startId, endId, startLabel, endLabel }];
  }
  if (!ranges) {
    return '';
  }

  const range = ranges[0];
  const { type: referenceType, startId, endId, startLabel, endLabel } = range;

  let referenceStartTypeOut = referenceType ? ` ${capitalize(referenceType.toLowerCase())}` : '';
  const referenceEndTypeOut = referenceType ? ` ${capitalize(referenceType.toLowerCase())}` : '';

  if (startId !== endId) {
    referenceStartTypeOut += 's';
  }

  let scopeOut = `${referenceStartTypeOut} ${startLabel}`;

  if (referenceType !== referenceType || startId !== endId) {
    scopeOut += `- `;
  }

  if (referenceType !== referenceType) {
    scopeOut += `${referenceEndTypeOut}`;
  }

  if (startId !== endId) {
    scopeOut += `${endLabel}`;
  }

  return `${scopeOut}${typeOut}`;
};

export const translateAssignmentFromTransport = (attributes: any): any => {
  const { book } = attributes;
  if (!book) {
    return attributes;
  }

  return {
    ...attributes,
    book: translateBookFromTransport(book),
    content: translateContentFromTransport(attributes.content),
  };
};

export const translateAssignmentForTransport = (attributes: any): any => {
  const { book } = attributes;
  if (!book) {
    return attributes;
  }

  return {
    ...attributes,
    book: translateBookForTransport(book),
  };
};

export const getAssignmentDescription = (
  assignment: Assignment,
  bookOutline?: BookOutline | ProductBookOutline,
  contentOutline?: ContentOutline
): string => {
  if (bookOutline) {
    let range = '';

    range = getRangeWithRanges(assignment, bookOutline?.chapters ?? [], bookOutline?.pages ?? []);

    return `${range} ${displayAssignmentType(assignment?.type)}`;
  } else if (contentOutline) {
    let range = '';

    range = getRangeWithRanges(
      assignment,
      contentOutline?.chapters ?? [],
      contentOutline?.pages ?? []
    );

    return `${range} ${displayAssignmentType(assignment?.type)}`;
  } else {
    return assignment.name;
  }
};

const getRangeWithRanges = (assignment: Assignment, chapters: Chapter[], pages: Page[]): string => {
  const { sources } = assignment;
  if (!sources) {
    return '';
  }

  const source = sources[0];
  if (!source) {
    return '';
  }

  let ranges: AssignmentRange[] = [];
  if (source) {
    const { startId, endId, startLabel, endLabel, referenceType } = source;
    ranges = [{ type: referenceType, startId, endId, startLabel, endLabel }];
  }

  if (!ranges) {
    return '';
  }

  let output = '';
  for (const range of ranges) {
    const { startId, endId, type, startLabel: _startLabel, endLabel: _endLabel } = range;

    if (startId === endId) {
      if (type === AssignmentReferenceType.PAGE) {
        const page = getPageById(pages, startId);
        output += `${i18n.t('page')} ${page?.label ?? '-'}`;
      } else if (type === AssignmentReferenceType.CHAPTER) {
        const chapter = getChapterById(chapters, startId);
        output += `${chapter?.label?.includes('chapter') ? '' : i18n.t('chapter')} ${chapter?.label ?? '-'}`;
      }
    } else {
      if (type === AssignmentReferenceType.PAGE) {
        const startPage = getPageById(pages, startId);
        const endPage = getPageById(pages, endId);
        output += `${i18n.t('pages')} ${startPage?.label ?? '-'} - ${endPage?.label ?? '-'}`;
      } else if (type === AssignmentReferenceType.CHAPTER) {
        const startChapter = getChapterById(chapters, startId);
        const endChapter = getChapterById(chapters, endId);
        output += `${i18n.t('chapters')} ${startChapter?.label ?? '-'} - ${endChapter?.label ?? '-'}`;
      }
    }

    output += ', ';
  }

  return output;
};

export const getStartId = (assignment: Assignment, product?: Product) => {
  if (!product) {
    return 0;
  }

  const { sources } = assignment;

  const source = sources?.find((source: AssignmentSource) => source.productId === product.id);

  if (!source) {
    return 0;
  }

  const ranges: AssignmentRange[] = [];
  if (source) {
    const { startId, endId, referenceType, startLabel, endLabel } = source;
    ranges.push({ type: referenceType, startId, endId, startLabel, endLabel });
  }
  if (!ranges) {
    return 0;
  }

  const range = ranges[0];
  return range.startId;
};

export const isAssignmentAvailable = (assignment: Assignment) => {
  if (!assignment?.availableAt) {
    return false;
  }

  return new Date(assignment.availableAt).getTime() < new Date().getTime();
};

export const addExtensionsToDate = (
  dateIn: Date,
  assignment: Assignment,
  enrollment?: Enrollment
): Date => {
  const date = new Date(dateIn);

  if (!enrollment || !assignment?.id) {
    return date;
  }

  const { extensions } = enrollment ?? {};
  const { extendedDueDates } = extensions ?? {};
  if (!extendedDueDates) {
    return date;
  }

  // All assignments have an extension;
  const allExtendedDueDates = extendedDueDates[0];
  const specificExtendedDueDate = extendedDueDates[assignment.id];

  const extendedDueDate = specificExtendedDueDate ?? allExtendedDueDates;
  if (extendedDueDate) {
    const { timeDelimiter, value } = extendedDueDate;
    let offset = 0;

    switch (timeDelimiter) {
      case TimeDelimiter.Days:
        offset = value * 24 * 60 * 60 * 1000; // Convert days to milliseconds
        break;
      case TimeDelimiter.Hours:
        offset = value * 60 * 60 * 1000; // Convert hours to milliseconds
        break;
    }

    return new Date(date.getTime() + offset);
  }

  return date;
};

export const isAssignmentOpen = (
  assignment: Assignment,
  courseSection: CourseSection,
  enrollment?: Enrollment
) => {
  if (!isAssignmentAvailable(assignment)) {
    return false;
  }

  const { availabilityEndBasis = AvailabilityEndBasis.DUE_AT } = courseSection ?? {};

  let dateToCompare: Date | null = null;

  switch (availabilityEndBasis) {
    case AvailabilityEndBasis.DUE_AT:
      dateToCompare = new Date(assignment.dueAt);
      break;

    case AvailabilityEndBasis.GRADE_AT:
      dateToCompare = new Date(assignment.gradeAt);
      break;

    default:
      return false;
  }

  if (!dateToCompare || isNaN(dateToCompare.getTime())) {
    return false;
  }

  return (
    addExtensionsToDate(dateToCompare, assignment, enrollment).getTime() > new Date().getTime()
  );
};

export const getAssignmentExtensions = ({
  assignment,
  enrollment,
}: {
  assignment: Assignment;
  enrollment: Enrollment | undefined;
}) => {
  const getTimeLimit = () => {
    if (!enrollment) {
      return assignment.timeLimit;
    }

    const { extensions } = enrollment;
    const { extraQuizTime, extraTestTime } = extensions ?? {};

    let assignmentExtraQuizTime = 0;

    if (assignment.type === AssignmentType.QUIZ && extraQuizTime) {
      if (assignment.id && extraQuizTime[assignment.id]) {
        assignmentExtraQuizTime = extraQuizTime[assignment.id];
      }

      let allAssignmentExtraQuizTime = 0;

      if (extraQuizTime[0]) {
        allAssignmentExtraQuizTime = extraQuizTime[0];
      }

      const maxExtraTime = Math.max(assignmentExtraQuizTime, allAssignmentExtraQuizTime);

      return assignment.timeLimit + maxExtraTime;
    } else if (assignment.type === AssignmentType.TEST && extraTestTime) {
      let assignmentExtraTestTime = 0;

      if (assignment.id && extraTestTime[assignment.id]) {
        assignmentExtraTestTime = extraTestTime[assignment.id];
      }

      let allAssignmentExtraTestTime = 0;

      if (extraTestTime[0]) {
        allAssignmentExtraTestTime = extraTestTime[0];
      }

      const maxExtraTime = Math.max(assignmentExtraTestTime, allAssignmentExtraTestTime);

      return assignment.timeLimit + maxExtraTime;
    }

    return assignment.timeLimit;
  };

  const getDueAt = () => {
    if (!enrollment?.extensions?.extendedDueDates) {
      return assignment.gradeAt;
    }

    const { extensions } = enrollment;
    const { extendedDueDates } = extensions ?? {};

    let assignmentExtendedDueDate = { value: 0, timeDelimiter: TimeDelimiter.Hours };
    let assignmentDateDelta = 0;

    if (assignment.id && extendedDueDates[assignment.id]) {
      assignmentExtendedDueDate = extendedDueDates[assignment.id];
      if (assignmentExtendedDueDate.timeDelimiter && assignmentExtendedDueDate.value) {
        assignmentDateDelta =
          assignmentExtendedDueDate.timeDelimiter === TimeDelimiter.Hours
            ? Number(assignmentExtendedDueDate.value) * 60 * 60 * 1000
            : Number(assignmentExtendedDueDate.value) * 24 * 60 * 60 * 1000;
      }
    }

    let allAssignmentExtendedDueDate = { value: 0, timeDelimiter: TimeDelimiter.Hours };
    let allDateDelta = 0;

    if (extendedDueDates[0]) {
      allAssignmentExtendedDueDate = extendedDueDates[0];

      if (allAssignmentExtendedDueDate.timeDelimiter && allAssignmentExtendedDueDate.value) {
        allDateDelta =
          allAssignmentExtendedDueDate.timeDelimiter === TimeDelimiter.Hours
            ? Number(allAssignmentExtendedDueDate.value) * 60 * 60 * 1000
            : Number(allAssignmentExtendedDueDate.value) * 24 * 60 * 60 * 1000;
      }
    }

    const maxExtraTime = Math.max(allDateDelta, assignmentDateDelta);

    return new Date(new Date(assignment.gradeAt).getTime() + maxExtraTime);
  };

  const getMaxAttempts = () => {
    let extraQuizAttempts = 0;
    if (enrollment?.extensions?.extraQuizAttempts) {
      let extraQuizAttemptsForAllAssignments = 0;

      if (enrollment.extensions.extraQuizAttempts[0]) {
        extraQuizAttemptsForAllAssignments = Number(enrollment.extensions.extraQuizAttempts[0]);
      }

      let extraQuizAttemptsForThisAssignment = 0;

      if (assignment.id && enrollment.extensions.extraQuizAttempts[assignment.id]) {
        extraQuizAttemptsForThisAssignment = Number(
          enrollment.extensions.extraQuizAttempts[assignment.id]
        );
      }

      extraQuizAttempts = Math.max(
        extraQuizAttemptsForAllAssignments,
        extraQuizAttemptsForThisAssignment
      );
    }

    return assignment.maxAttempts + extraQuizAttempts;
  };

  const timeLimit = getTimeLimit();
  const gradeAt = getDueAt();

  const maxAttempts = getMaxAttempts();
  const remainingAttemptCount = maxAttempts - (assignment.attempts ?? []).length;

  const isAvailable =
    new Date(assignment.availableAt).getTime() < new Date().getTime() &&
    new Date(gradeAt).getTime() > new Date().getTime();

  const gradeAtLabel = isAvailable
    ? `${displayDate(getDueAt(), i18n.language, false)}`
    : `${i18n.t('closed')}`;

  const canContinue = isAvailable && !!assignment.attempts?.some(isActiveAttempt);
  const canStart =
    isAvailable &&
    (assignment.type === AssignmentType.QUIZ
      ? remainingAttemptCount > 0
      : assignment.type === AssignmentType.TEST
        ? remainingAttemptCount > 0 && !canContinue
        : !assignment.attempts?.length || canContinue);

  const latestAttempt = [...(assignment.attempts ?? [])]
    .sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }

      return b.attemptNumber ?? 0 - (a.attemptNumber ?? 0);
    })
    .shift();

  const progress = latestAttempt
    ? ((latestAttempt.numberOfCorrectAnswers + latestAttempt.numberOfIncorrectAnswers) /
        latestAttempt.questionIds.length) *
      100
    : 0;

  return {
    gradeAt,
    gradeAtLabel,
    maxAttempts,
    remainingAttemptCount,
    timeLimit,
    canContinue,
    canStart,
    progress,
  };
};

export const sortAssignments = (direction: Direction) => (a: Assignment, b: Assignment) => {
  const sortMultiplier = direction === Direction.ASC ? 1 : -1;

  const getDueSortValue = () => {
    // If either assignment does not have a dueAt, we consider them equal
    if (!a.dueAt || !b.dueAt) {
      return 0;
    }

    return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
  };

  const sortValue = getDueSortValue();
  if (sortValue !== 0) {
    return sortValue * sortMultiplier;
  }

  const getAssignmentTypeSortValue = () => {
    switch (true) {
      case a.type === AssignmentType.READING_COMPREHENSION:
        return -1;
      case b.type === AssignmentType.READING_COMPREHENSION:
        return 1;
      case a.type === AssignmentType.STUDY_GUIDE:
        return -1;
      case b.type === AssignmentType.STUDY_GUIDE:
        return 1;
      case a.type === AssignmentType.TUTOR:
        return -1;
      case b.type === AssignmentType.TUTOR:
        return 1;
      default:
        return 0;
    }
  };

  // If the dueAt dates are the same, sort by module index or moduleId
  const getModuleSortValue = () => {
    if (a.module && b.module) {
      return a.module.index - b.module.index;
    }

    if (a.moduleId && b.moduleId) {
      return a.moduleId !== b.moduleId ? a.moduleId - b.moduleId : getAssignmentTypeSortValue();
    }

    return 0;
  };

  const moduleSortValue = getModuleSortValue();
  return moduleSortValue * sortMultiplier;
};

export const isPromptAssignment = (assignment: Assignment) => {
  return assignment.type === AssignmentType.DISCUSSION || assignment.type === AssignmentType.TUTOR;
};
